export type Language = 'en' | 'fr';

type Dictionary = Record<string, string>;

const dictionaries: Record<Language, Dictionary> = {
  en: {
    'language.label': 'Language',
    'language.en': 'English',
    'language.fr': 'French',
    'brand.name': 'TeaTime',
    'role.admin': 'Admin',
    'role.worker': 'Worker',
    'common.signOut': 'Sign out',
    'common.retry': 'Retry',
    'common.loading': 'Loading...',
    'app.nav.overview': 'Overview',
    'app.nav.quick': 'Quick',
    'app.nav.workers': 'Workers',
    'app.nav.attendance': 'Attendance',
    'app.nav.salary': 'Salary',
    'app.nav.grades': 'Grades',
    'app.nav.management': 'Shop Management',
    'app.nav.settings': 'Settings',
    'app.nav.checkin': 'Check In / Out',
    'app.nav.myAttendance': 'My Attendance',
    'app.nav.mySalary': 'My Salary',
    'app.page.adminDashboard': 'Admin Dashboard',
    'app.page.workerPortal': 'Worker Portal',
    'app.subtitle.loadingLiveData': 'Loading live data...',
    'app.subtitle.loadingAccount': 'Loading your account...',
    'app.subtitle.loadingAttendanceData': 'Loading your attendance data...',
    'app.subtitle.redirecting': 'Redirecting...',
    'app.subtitle.redirectingWorkspace': 'Redirecting to the correct workspace...',
    'app.subtitle.adminLiveData': 'Live attendance, payroll, and worker data for your shop.',
    'app.subtitle.adminLiveDataWithShop': '{shop} | Live attendance, payroll, and worker data',
    'app.subtitle.workerPortal':
      'Track your check-ins, attendance history, and salary in real time.',
    'auth.signedInSuccess': 'Signed in successfully.',
    'auth.unableSignIn': 'Unable to sign in.',
    'auth.title': 'Sign in',
    'auth.subtitle': 'Enter your email and password to access your workspace.',
    'auth.restoringSession': 'Restoring your session...',
    'auth.emailLabel': 'Email address',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.emailRequired': 'Email is required',
    'auth.emailInvalid': 'Enter a valid email address',
    'auth.passwordLabel': 'Password',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.passwordRequired': 'Password is required',
    'auth.passwordMin': 'Password must be at least 6 characters',
    'auth.signingIn': 'Signing in...',
    'auth.signIn': 'Sign in',
    'auth.leftTitleLine1': 'Attendance and payroll',
    'auth.leftTitleLine2': 'for real shop operations',
    'auth.leftDescription':
      'Sign in with your account to manage workers, track attendance, and compute payroll.',
    'auth.secureSignIn': 'Secure sign-in',
    'auth.secureSignInDesc': 'Sessions are protected for both admins and workers.',
    'auth.roleBasedAccess': 'Role-based access',
    'auth.roleBasedAccessDesc':
      'Admins manage the shop while workers only see their own attendance data.',
    'auth.footerHelp': 'Use the worker or admin accounts created for your shop.',
  },
  fr: {
    'language.label': 'Langue',
    'language.en': 'Anglais',
    'language.fr': 'Francais',
    'brand.name': 'TeaTime',
    'role.admin': 'Admin',
    'role.worker': 'Employe',
    'common.signOut': 'Se deconnecter',
    'common.retry': 'Reessayer',
    'common.loading': 'Chargement...',
    'app.nav.overview': 'Apercu',
    'app.nav.quick': 'Rapide',
    'app.nav.workers': 'Employes',
    'app.nav.attendance': 'Presence',
    'app.nav.salary': 'Salaire',
    'app.nav.grades': 'Grades',
    'app.nav.management': 'Gestion du magasin',
    'app.nav.settings': 'Parametres',
    'app.nav.checkin': 'Pointage Entree / Sortie',
    'app.nav.myAttendance': 'Ma presence',
    'app.nav.mySalary': 'Mon salaire',
    'app.page.adminDashboard': 'Tableau Admin',
    'app.page.workerPortal': 'Portail Employe',
    'app.subtitle.loadingLiveData': 'Chargement des donnees en direct...',
    'app.subtitle.loadingAccount': 'Chargement de votre compte...',
    'app.subtitle.loadingAttendanceData': 'Chargement de vos donnees de presence...',
    'app.subtitle.redirecting': 'Redirection...',
    'app.subtitle.redirectingWorkspace': 'Redirection vers l espace correct...',
    'app.subtitle.adminLiveData':
      'Presence, paie et donnees employes en temps reel pour votre magasin.',
    'app.subtitle.adminLiveDataWithShop':
      '{shop} | Presence, paie et donnees employes en temps reel',
    'app.subtitle.workerPortal':
      'Suivez vos pointages, votre historique de presence et votre salaire en temps reel.',
    'auth.signedInSuccess': 'Connexion reussie.',
    'auth.unableSignIn': 'Connexion impossible.',
    'auth.title': 'Connexion',
    'auth.subtitle': 'Entrez votre e-mail et mot de passe pour acceder a votre espace.',
    'auth.restoringSession': 'Restauration de votre session...',
    'auth.emailLabel': 'Adresse e-mail',
    'auth.emailPlaceholder': 'vous@exemple.com',
    'auth.emailRequired': "L'e-mail est requis",
    'auth.emailInvalid': 'Entrez une adresse e-mail valide',
    'auth.passwordLabel': 'Mot de passe',
    'auth.passwordPlaceholder': 'Entrez votre mot de passe',
    'auth.passwordRequired': 'Le mot de passe est requis',
    'auth.passwordMin': 'Le mot de passe doit contenir au moins 6 caracteres',
    'auth.signingIn': 'Connexion...',
    'auth.signIn': 'Se connecter',
    'auth.leftTitleLine1': 'Presence et paie',
    'auth.leftTitleLine2': 'pour un vrai magasin',
    'auth.leftDescription':
      'Connectez-vous pour gerer les employes, suivre la presence et calculer la paie.',
    'auth.secureSignIn': 'Connexion securisee',
    'auth.secureSignInDesc': 'Les sessions sont protegees pour les admins et les employes.',
    'auth.roleBasedAccess': 'Acces selon le role',
    'auth.roleBasedAccessDesc':
      'Les admins gerent le magasin, les employes voient seulement leurs donnees.',
    'auth.footerHelp': 'Utilisez les comptes admin ou employe crees pour votre magasin.',
  },
};

export function translate(language: Language, key: string, vars?: Record<string, string | number>) {
  const template = dictionaries[language][key] ?? dictionaries.en[key] ?? key;
  if (!vars) return template;

  return Object.entries(vars).reduce(
    (result, [varKey, value]) => result.replaceAll(`{${varKey}}`, String(value)),
    template
  );
}
